import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import actions from './actions';
import tracking from '../../tracking/tracking.js';
import addressSearchConfig from '../../components/AddressSearch/config';
import Dropdown from '../../components/Dropdown/Dropdown.jsx';
import Modal from '../../components/Utils/Modal/Modal.jsx';
import AddressSearchResults from './AddressSearchResults.jsx';
import { DesktopAndTablet, Mobile } from '../../components/Utils/MediaComponents/MediaComponents';
import { userIntents, propertyTypes } from '../../utils/userIntentConfig';
import { getPropertyType } from '../../realEstateTypes.js';
import { toCommercializationType } from '../../userIntents.js';
import { getRealEstateType, getUserIntent } from '../../selectors/selectors.js';
import { generateLink } from '../../utils/suggestionsUtils';
import featureSwitchReaderFactory from '../../utils/featureSwitchReader';
import styles from './AddressSearch.less';
import { isRenderedInBrowser } from '../../store/configureStore';
import { PROPERTY_BOOK } from "../../tracking/trackingNaming";
import { USE_STICKY_BUTTON } from "../../utils/featureFlags";

const isFeatureOn = featureSwitchReaderFactory.getInstance().isFeatureOn;

export class AddressSearch extends React.Component {
  static flattenSuggestionResult(suggestionResults) {
    return Object.keys(addressSearchConfig).reduce((acc, configItem) => {
      const values = suggestionResults[configItem];
      return values ? [...acc, ...values] : acc;
    }, []);
  }

  constructor(props) {
    super(props);
    this.state = { searchInput: '', showStickyButton: false };

    this.Modal = React.createRef();
    this.modalInput = React.createRef();
    this.myRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
  }

  componentDidMount() {
    document.addEventListener('scroll', this.checkSticky);
    document.addEventListener('resize', this.checkSticky);
    this.checkSticky();
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.checkSticky);
  }

  checkSticky = () => {
    if (!this.myRef.current) {
      return;
    }
    const rect = this.myRef.current.getBoundingClientRect();
    if (!rect) {
      return;
    }
    if (rect.y < -20) {
      this.setState({ showStickyButton: true });
    } else {
      this.setState({ showStickyButton: false });
    }
  };

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { singleSuggestionResult } = nextProps;
    const shouldRedirect = !!singleSuggestionResult && !!singleSuggestionResult.url;

    if (shouldRedirect) {
      this.trackAndNavigate(singleSuggestionResult.url);
    }
  }

  scrollToTop = () =>{
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  handleSelectSuggestion = (url) => (event) => {
    event.preventDefault();
    this.props.cancelSearch();
    this.trackAndNavigate(url);
  };

  trackAndNavigate = (url) => {
    tracking.report(PROPERTY_BOOK).then(() => {
      window.location.assign(url);
    });
  };

  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.props.cancelSearch();
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.props.suggestionsArrowUpEvent();
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.props.suggestionsArrowDownEvent();
    }
    if (event.key === 'Enter') {
      event.preventDefault();

      const suggestions = AddressSearch.flattenSuggestionResult(this.props.suggestionResults);

      this.handleSelectSuggestion(
        generateLink(suggestions[this.props.selectedSuggestionIndex], this.props.suggestionQuery),
      )(event);
    }
  };

  handleSearchInput = (event) => {
    this.props.updateSuggestionQuery(event.target.value);
  };

  handleChange = (event) => {
    this.setState({ searchInput: event.target.value });
  };

  handlePropertyTypeChange = (event) => {
    const value = event.target.value;

    this.props.selectRealEstateType(`${value}_${toCommercializationType(this.props.userIntent)}`);
  };

  handleUserIntentChange = (event) => {
    const value = event.currentTarget.value;

    this.props.selectUserIntent(value);
    this.props.selectRealEstateType(`${this.props.propertyType}_${toCommercializationType(value)}`);
  };

  openModal = (event) => {
    event.target.blur();
    event.preventDefault();
    if (!this.Modal.current.isOpened()) {
      this.Modal.current.open();
    }
  };

  afterOpenModalHandler = () => {
    this.modalInput.current.focus();
  };

  render() {
    const { propertyType, userIntent } = this.props;

    return (
      <div id="address-search-box">
        {isRenderedInBrowser() && (
          <>
          {isFeatureOn(USE_STICKY_BUTTON) &&
            <DesktopAndTablet
            render={() => (
              <div
                data-visible={this.state.showStickyButton ? 'yes' : 'no'}
                className={`${styles.stickyButtonLine} align-center`}
              >
                <button
                  data-qa="stickyButton"
                  type="button"
                className="button-primary"
                onClick={(event) => this.scrollToTop() }>
                  Preise für meine Nachbarschaft prüfen
                </button>
              </div>
            )}
          />}
          <Mobile
          render={() => (
          <div
            data-visible={this.state.showStickyButton ? 'yes' : 'no'}
            className={`${styles.stickyButtonLine}`}
          >
            <div className="grid grid-flex gutter-m grid-fill-rows">
              <div className="grid-item one-third">
                <input
                  type="text"
                  placeholder="Stadt, Stadtviertel oder Adresse eingeben"
                  className="grid-item padding-left-m padding-right-m font-ellipsis one-whole"
                  autoComplete="off"
                  onFocus={(event) => this.openModal(event)}
                />
              </div>
              <div className="grid-item grid-item-fixed-width">
                <button type="button" className="button-primary" onClick={(event) => this.openModal(event)}>
                  Preise prüfen
                </button>
              </div>
            </div>
          </div>
          )}
          />
          </>
        )}

        <div className={`grid ${styles.searchContainer}`}>
          <p
            className={`grid-item one-whole font-bold font-regular margin-bottom-xs palm-margin-bottom-m ${styles.bigHeadline}`}
          >
            Entdecken Sie Ihren lokalen Immobilienmarkt
          </p>
          <p
            className={`grid-item one-whole font-lightgray margin-bottom-m palm-margin-bottom-xl ${styles.subHeadline}`}
          >
            Basierend auf über 6 Milliarden Immobiliendaten
          </p>
          <div className="search-section grid-item desk-four-fifths lap-four-fifths palm-one-whole">
            <div className="grid grid-flex">
              <div className={`grid-item ${styles.searchInputWrapper} one-whole`}>
                <div className="grid grid-flex">
                  <div className="grid-item desk-one-fifth lap-one-fifth palm-hide">
                    <Dropdown
                      id="propertyTypesDropdown"
                      options={propertyTypes}
                      selected={propertyType}
                      onChangeHandler={this.handlePropertyTypeChange}
                    />
                  </div>
                  <DesktopAndTablet
                    render={() => (
                      <div className="grid-item desk-three-fifths lap-three-fifths palm-one-whole absolute-reference">
                        <input
                          type="text"
                          placeholder="Stadt, Stadtviertel oder Adresse eingeben"
                          className="one-whole padding-left-m"
                          autoComplete="off"
                          autoFocus
                          onFocus={() =>
                            tracking.report(`index_search_enteraddress_${userIntent.toLowerCase()}`, PROPERTY_BOOK)
                          }
                          onBlur={() => this.props.cancelSearch()}
                          onInput={(e) => this.handleSearchInput(e)}
                          onKeyDown={(e) => this.handleKeyDown(e)}
                          value={this.state.searchInput}
                          onChange={this.handleChange}
                        />
                        <AddressSearchResults />
                      </div>
                    )}
                  />
                  <Mobile
                    render={() => (
                      <input
                        type="text"
                        placeholder="Stadt, Stadtviertel oder Adresse eingeben"
                        className="grid-item padding-left-m desk-three-fifths lap-three-fifths palm-one-whole"
                        autoFocus
                        autoComplete="off"
                        onFocus={(event) => this.openModal(event)}
                      />
                    )}
                  />
                  <div className="grid-item desk-one-fifth lap-one-fifth palm-hide">
                    <Dropdown
                      id="userIntentTypesDropdown"
                      options={userIntents}
                      selected={userIntent}
                      onChangeHandler={this.handleUserIntentChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DesktopAndTablet
            render={() => (
              <button
                type="submit"
                ref={this.myRef}
                className="button-primary grid-item desk-one-fifth lap-one-fifth  palm-one-whole">
                Preise prüfen
              </button>
            )}
          />
          <Mobile
            render={() => (
              <button
                type="submit"
                ref={this.myRef}
                className="button-primary grid-item desk-one-fifth lap-one-fifth  palm-one-whole"
                onClick={(event) => this.openModal(event)}
              >
                Preise prüfen
              </button>
            )}
          />

          {isRenderedInBrowser() && (
            <Mobile
              render={() => (
                <Modal
                  ref={this.Modal}
                  title="Suchen"
                  className={styles.addressSearchModalContainer}
                  isFeatureOn={isFeatureOn}
                  afterOpenHandler={this.afterOpenModalHandler}
                >
                  <div className={styles.mainContainer} onClick={() => this.props.cancelSearch()}>
                    <div className="grid">
                      <div className="grid-item one-whole tabs-container">
                        <div className="tab-list" role="tablist" aria-label="user-intend">
                          {userIntents.map((item, index) => (
                            <button
                              type="submit"
                              className="tab grid-item one-fourth font-bold font-regular font-s"
                              qa-regression-tag="tab"
                              role="tab"
                              aria-selected={userIntent === item.value}
                              key={index}
                              id={`tab-${item.value}`}
                              aria-controls={`tabpanel-${item.value}`}
                              value={item.value}
                              onClick={this.handleUserIntentChange}
                            >
                              <div className="one-whole align-center">{item.name}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid-item one-whole margin-top-l">
                        <Dropdown
                          id="propertyTypesDropdown"
                          options={propertyTypes}
                          selected={propertyType}
                          onChangeHandler={this.handlePropertyTypeChange}
                        />
                      </div>
                      <div className={`grid-item ${styles.searchInputWrapper} one-whole margin-top-l`}>
                        <input
                          data-qa=""
                          type="text"
                          placeholder="Stadt, Stadtviertel oder Adresse eingeben"
                          className="grid-item one-whole padding-left-m"
                          autoComplete="off"
                          onFocus={() =>
                            tracking.report(`index_search_enteraddress_${userIntent.toLowerCase()}`, PROPERTY_BOOK)
                          }
                          onInput={(e) => this.handleSearchInput(e)}
                          onKeyDown={(e) => this.handleKeyDown(e)}
                          value={this.state.searchInput}
                          onChange={this.handleChange}
                          ref={this.modalInput}
                        />
                      </div>
                      <div className="grid-item absolute-reference one-whole">
                        <div
                          className={styles.addressResultsContainer}
                          onTouchMove={() => {
                            this.modalInput.current.blur();
                          }}
                        >
                          <AddressSearchResults />
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="button-primary grid-item one-whole margin-top-l"
                      onClick={this.handleClick}
                    >
                      Preise prüfen
                    </button>
                  </div>
                </Modal>
              )}
            />
          )}
        </div>
      </div>
    );
  }
}

AddressSearch.propTypes = {
  propertyType: PropTypes.string.isRequired,
  userIntent: PropTypes.string.isRequired,
  cancelSearch: PropTypes.func,
  updateSuggestionQuery: PropTypes.func,
  suggestionsArrowDownEvent: PropTypes.func,
  suggestionsArrowUpEvent: PropTypes.func,
  selectRealEstateType: PropTypes.func,
  selectUserIntent: PropTypes.func,
  suggestionResults: PropTypes.object,
  suggestionQuery: PropTypes.string,
  selectedSuggestionIndex: PropTypes.number,
};

const mapDispatchToProps = {
  cancelSearch: () => actions.cancelAddressSearch(),
  updateSuggestionQuery: (suggestionQuery) => actions.updateSuggestionQuery(suggestionQuery),
  suggestionsArrowDownEvent: () => actions.suggestionsArrowDownEvent(),
  suggestionsArrowUpEvent: () => actions.suggestionsArrowUpEvent(),
  selectRealEstateType: (value) => actions.selectRealEstateType(value),
  selectUserIntent: (value) => actions.selectUserIntent(value),
};

const mapStateToProps = (state) => ({
  ...state.indexpage,
  propertyType: getPropertyType(getRealEstateType(state)),
  userIntent: getUserIntent(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddressSearch);
